import { lazy, Suspense, useEffect } from "react";
import {
  RouteObject,
  useParams,
  useRoutes,
  useSearchParams,
} from "../../helpers/tixxt-router";
import { at, defer } from "lodash";
import invariant from "invariant";
import documentReady from "../../helpers/documentReady";
import TixxtGroupNavigation from "../layout/TixxtGroupNavigation";
import TixxtPageTitle from "../layout/TixxtPageTitle";

const AppointmentPageTitle = lazy(() =>
  import("../appointments/AppointmentPageTitle").then((module) => ({
    default: module.AppointmentPageTitle,
  })),
);
import {
  startFeature,
  stopFeature,
} from "../../../assets/javascripts/tixxt/start_feature";

type Props = {
  feature: string;
  action: string;
  paramKeys: string[];
};

function toggleBackbone(show: boolean) {
  document.getElementById("backbone-root")?.classList.toggle("hidden", !show);
}

export default function BackboneFeature({ feature, action, paramKeys }: Props) {
  const params = useParams();
  const actionArguments = at(params, paramKeys);

  const [searchParams] = useSearchParams();
  const query = {};
  searchParams.forEach((k, v) => (query[v] = k));

  useEffect(() => {
    toggleBackbone(true);
    return function () {
      toggleBackbone(false);
    };
  }, []);

  useEffect(() => {
    return function () {
      stopFeature();
    };
  }, [feature]);

  useEffect(() => {
    documentReady(() =>
      defer(() => {
        startFeature(feature, action, [...actionArguments, query]);
      }),
    );
  }, [feature, action, params, query]);

  const elem = useRoutes([
    {
      path: "/appointments/:appointmentId",
      element: (
        <Suspense fallback={null}>
          <AppointmentPageTitle />
        </Suspense>
      ),
    },
    { path: "/groups/:groupSlug/*", element: <TixxtPageTitle /> },
    { path: "/activitystreams/:streamSlug/*", element: <TixxtPageTitle /> },
    { path: "*", element: <TixxtPageTitle /> },
  ]);

  return (
    <>
      {elem}
      <TixxtGroupNavigation />
    </>
  );
}

export function convertRoutes({ feature, featureRoutes }): RouteObject[] {
  const paramRegex = /:(\w+)/g;

  function route(
    path: string,
    action: string,
    omittedParamKeys: string[] = [],
  ) {
    const paramKeys = Array.from(
      path.matchAll(paramRegex),
      ([, capture]) => capture,
    );
    return {
      path,
      element: (
        <BackboneFeature
          feature={feature}
          action={action}
          paramKeys={omittedParamKeys.concat(paramKeys)}
        />
      ),
    };
  }

  return Object.keys(featureRoutes).flatMap((path) => {
    const action = featureRoutes[path];
    // path with optional group slug
    if (path.startsWith("(groups/:group_slug/)")) {
      const networkPath = path.replace("(groups/:group_slug/)", "");
      const groupPath = path.replace(
        "(groups/:group_slug/)",
        "/groups/:group_slug/",
      );
      return [
        route("/" + networkPath, action, ["group_slug"]),
        route(groupPath, action),
      ];
    }

    invariant(
      path.indexOf("(") == -1,
      `path for convertRoutes must not contain other optional segments, got "${path}"`,
    );

    // regular path
    return [route("/" + path, action)];
  });
}
